import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
  Button,
  Tooltip,
  Backdrop,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArchiveIcon from '@mui/icons-material/Archive';
import { Card, Pill, SectionHeader } from '../shared';
import { SAGE, CREAM, WARM } from '../../theme';

import {
  getProfile,
  updateProfile,
  updatePersonalInfo,
  exportProfile,
  importProfile
} from '../../services/profileManagerService';
// Note: Other functions commented out until tabs are built
// addSummary, updateSummary, deleteSummary,
// addExperience, updateExperience, deleteExperience,
// addHighlight, updateHighlight, deleteHighlight,
// addEducation, updateEducation, deleteEducation,
// updateSkills, addProject, updateProject, deleteProject

// Import tab components
import PersonalInfoTab from './PersonalInfoTab';
import SummariesTab from './SummariesTabV2';
import ExperienceTab from './ExperienceTabV2';
import HighlightsTab from './HighlightsTabV2';
import EducationTab from './EducationTabV2';
import SkillsTab from './SkillsTabV2';
import ProjectsTab from './ProjectsTabV2';
import { API_URL } from '../../config';
import { mockAuthHeaders } from '../../utils/mockAuth';

// Tab Panel component
function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} role="tabpanel">
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ProfileManager = () => {
  // State
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [dirtyTabs, setDirtyTabs] = useState({});

  // Track dirty state for each tab
  const handleTabDirtyChange = (tabName, isDirty) => {
    setDirtyTabs(prev => ({ ...prev, [tabName]: isDirty }));
  };

  const handleTabChange = (event, newValue) => {
    // Check if current tab has unsaved changes
    const currentTabName = tabs[activeTab].label.toLowerCase();
    if (dirtyTabs[currentTabName]) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to switch tabs?')) {
        return;
      }
    }
    setActiveTab(newValue);
  };
  const [saveStatus, setSaveStatus] = useState(''); // 'saving', 'saved', 'error'
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Resume import state
  const [resumeImporting, setResumeImporting] = useState(false);
  const [resumeImportStage, setResumeImportStage] = useState('');
  
  // Archive & Reset state
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);

  // Refs for auto-save
  const saveTimeoutRef = useRef(null);
  const hasUnsavedChanges = useRef(false);

  // Load profile on mount
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getProfile();

      // Ensure all required fields exist
      const completeProfile = {
        personal: data.personal || {},
        summaries: data.summaries || [],
        experience: data.experience || [],
        highlights: data.highlights || [],
        education: data.education || [],
        skills: data.skills || { technical: [], soft: [], languages: [], tools: [] },
        projects: data.projects || [],
        custom_sections: data.custom_sections || [],
        other: data.other || []
      };
      
      setProfile(completeProfile);
      hasUnsavedChanges.current = false;
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Failed to load profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-save with debouncing
  const scheduleAutoSave = useCallback(() => {
    hasUnsavedChanges.current = true;
    setSaveStatus('unsaved');

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Schedule save after 2 seconds of inactivity
    saveTimeoutRef.current = setTimeout(() => {
      saveProfile();
    }, 2000);
  }, []);

  const saveProfile = async () => {
    if (!profile || !hasUnsavedChanges.current) return;

    try {
      setSaving(true);
      setSaveStatus('saving');

      // Save the entire profile
      await updateProfile(profile);
      
      setSaveStatus('saved');
      hasUnsavedChanges.current = false;
      
      // Clear "saved" status after 2 seconds
      setTimeout(() => {
        if (!hasUnsavedChanges.current) {
          setSaveStatus('');
        }
      }, 2000);
    } catch (err) {
      console.error('Error saving profile:', err);
      setSaveStatus('error');
      setSnackbar({
        open: true,
        message: 'Failed to save profile',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Immediate save (for tab commits)
  const saveProfileImmediate = async (profileData) => {
    try {
      setSaving(true);
      setSaveStatus('saving');

      
      // Ensure all required fields are present
      const completeProfile = {
        personal: profileData.personal || {},
        summaries: profileData.summaries || [],
        experience: profileData.experience || [],
        highlights: profileData.highlights || [],
        education: profileData.education || [],
        skills: profileData.skills || { technical: [], soft: [], languages: [], tools: [] },
        projects: profileData.projects || [],
        custom_sections: profileData.custom_sections || [],
        other: profileData.other || []
      };

      // Save the entire profile
      const result = await updateProfile(completeProfile);
      
      setSaveStatus('saved');
      hasUnsavedChanges.current = false;
      
      setSnackbar({
        open: true,
        message: 'Profile saved successfully',
        severity: 'success'
      });
      
      // Clear "saved" status after 2 seconds
      setTimeout(() => {
        if (!hasUnsavedChanges.current) {
          setSaveStatus('');
        }
      }, 2000);
    } catch (err) {
      console.error('Error saving profile:', err);
      setSaveStatus('error');
      setSnackbar({
        open: true,
        message: 'Failed to save changes',
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  // Manual save
  const handleManualSave = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveProfile();
  };

  // Export/Import handlers
  const handleExport = async () => {
    try {
      const blob = await exportProfile();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `profile_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      setSnackbar({
        open: true,
        message: 'Profile exported successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error exporting profile:', err);
      setSnackbar({
        open: true,
        message: 'Failed to export profile',
        severity: 'error'
      });
    }
  };

  const handleImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      await importProfile(data);
      await loadProfile(); // Reload from server
      
      setSnackbar({
        open: true,
        message: 'Profile imported successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error importing profile:', err);
      setSnackbar({
        open: true,
        message: 'Failed to import profile. Please check the file format.',
        severity: 'error'
      });
    }
  };

  // Handle resume import
  const handleResumeImport = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset file input
    event.target.value = null;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setSnackbar({
        open: true,
        message: 'Please upload a PDF file',
        severity: 'error'
      });
      return;
    }

    setResumeImporting(true);
    setResumeImportStage('📄 Uploading resume...');

    try {

      // Upload PDF to backend
      const formData = new FormData();
      formData.append('file', file);

      setResumeImportStage('🤖 AI parsing resume...');

      // Set 3-minute timeout for AI processing (can take 30-90 seconds)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 180000); // 3 minutes

      try {
        const response = await fetch(`${API_URL}/api/automation/parse-resume-to-profile`, {
          method: 'POST',
          headers: {
            ...mockAuthHeaders(),
          },
          body: formData,
          signal: controller.signal,
          credentials: 'include'  // Include cookies if needed
        });
        
        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.detail || 'Resume parsing failed');
        }

        const result = await response.json();

        if (!result.success) {
          throw new Error(result.error || 'Resume parsing failed');
        }


        // Backend has already merged the data into profile.json
        // Just reload the profile to get the updated data
        setResumeImportStage('✨ Reloading profile...');
        
        const updatedProfile = await getProfile();
        setProfile(updatedProfile);
        

        // Success message
        const statsMsg = `Added: ${result.stats.experience_added || 0} jobs, ${result.stats.education_added || 0} degrees, ${result.stats.skills_added || 0} skills`;
        setSnackbar({
          open: true,
          message: `✅ Resume imported successfully! ${statsMsg}`,
          severity: 'success'
        });

        
      } catch (fetchError) {
        // Handle abort/timeout errors specifically
        if (fetchError.name === 'AbortError') {
          throw new Error('Resume parsing timed out after 3 minutes. Please try again or use a smaller file.');
        }
        // Re-throw other errors to be caught by outer catch
        throw fetchError;
      }

    } catch (error) {
      console.error('[RESUME-IMPORT] ❌ Error:', error);
      setSnackbar({
        open: true,
        message: `Failed to import resume: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setResumeImporting(false);
      setResumeImportStage('');
    }
  };

  // Handle Archive & Reset
  const handleArchiveAndReset = async () => {
    setArchiving(true);
    setArchiveDialogOpen(false);
    
    try {
      const response = await fetch(`${API_URL}/api/profile-manager/archive-and-reset`, {
        method: 'POST',
        headers: {
          ...mockAuthHeaders(),
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Archive failed');
      }
      
      const result = await response.json();
      
      // Show success message
      setSnackbar({
        open: true,
        message: `✅ Archived ${result.archived_count} items to ${result.archive_path}. Starting fresh!`,
        severity: 'success'
      });
      
      // Reload the profile to show fresh state
      setTimeout(() => {
        window.location.reload();
      }, 1500);
      
    } catch (error) {
      console.error('[ARCHIVE] Error:', error);
      setSnackbar({
        open: true,
        message: `❌ Archive failed: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setArchiving(false);
    }
  };

  // Section update handlers
  const updatePersonalSection = (updates) => {
    setProfile(prev => ({
      ...prev,
      personal: { ...prev.personal, ...updates }
    }));
    scheduleAutoSave();
  };

  const updateSummariesSection = async (summaries) => {
    const updated = { ...profile, summaries };
    setProfile(updated);
    await saveProfileImmediate(updated);
  };

  const updateExperienceSection = async (experience) => {
    const updated = { ...profile, experience };
    setProfile(updated);
    await saveProfileImmediate(updated);
  };

  const updateHighlightsSection = async (highlights) => {
    const updated = { ...profile, highlights };
    setProfile(updated);
    await saveProfileImmediate(updated);
  };

  const updateEducationSection = async (education) => {
    const updated = { ...profile, education };
    setProfile(updated);
    await saveProfileImmediate(updated);
  };

  const updateSkillsSection = async (skills) => {
    const updated = { ...profile, skills };
    setProfile(updated);
    await saveProfileImmediate(updated);
  };

  const updateProjectsSection = async (projects) => {
    const updated = { ...profile, projects };
    setProfile(updated);
    await saveProfileImmediate(updated);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      // Save any pending changes before unmounting
      if (hasUnsavedChanges.current && profile) {
        saveProfile();
      }
    };
  }, [profile]);

  // Tab configuration
  const tabs = [
    { label: 'Personal', count: null },
    { label: 'Summaries', count: profile?.summaries?.length || 0 },
    { label: 'Experience', count: profile?.experience?.length || 0 },
    { label: 'Highlights', count: profile?.highlights?.length || 0 },
    { label: 'Education', count: profile?.education?.length || 0 },
    { label: 'Skills', count: null },
    { label: 'Projects', count: profile?.projects?.length || 0 }
  ];

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: SAGE[400] }} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
          <Button onClick={loadProfile} size="small" sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  // No profile state
  if (!profile) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="info">No profile found. Creating a new profile...</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h4"
          sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 600, color: SAGE[900] }}
        >
          Profile
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {/* Save status */}
          {saveStatus === 'saving' && (
            <Pill preset="warm" label="Saving..." icon={<CircularProgress size={12} />} />
          )}
          {saveStatus === 'saved' && (
            <Pill preset="sage" label="Saved" />
          )}
          {saveStatus === 'unsaved' && (
            <Pill preset="warm" label="Unsaved" />
          )}

          <Tooltip title="Save now">
            <IconButton
              onClick={handleManualSave}
              disabled={saving || !hasUnsavedChanges.current}
              sx={{ color: SAGE[500], '&:hover': { color: SAGE[700] } }}
              size="small"
            >
              <SaveIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Export profile">
            <IconButton onClick={handleExport} sx={{ color: SAGE[500] }} size="small">
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Import profile (JSON)">
            <IconButton component="label" sx={{ color: SAGE[500] }} size="small">
              <UploadIcon fontSize="small" />
              <input type="file" hidden accept=".json" onChange={handleImport} />
            </IconButton>
          </Tooltip>

          <Tooltip title="Import Resume (AI)">
            <span>
              <IconButton
                component="label"
                sx={{ color: WARM.accent }}
                size="small"
                disabled={resumeImporting}
              >
                <AutoAwesomeIcon fontSize="small" />
                <input type="file" hidden accept=".pdf" onChange={handleResumeImport} disabled={resumeImporting} />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Archive & Reset">
            <IconButton
              onClick={() => setArchiveDialogOpen(true)}
              sx={{ color: WARM.rose }}
              size="small"
              disabled={archiving}
            >
              <ArchiveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main Card */}
      <Card sx={{ overflow: 'hidden' }}>
        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: `1px solid ${SAGE[100]}`,
            bgcolor: CREAM[100],
            '& .MuiTab-root': {
              fontFamily: "'Outfit', sans-serif",
              fontWeight: 500,
              fontSize: '0.8rem',
              textTransform: 'none',
              color: SAGE[500],
              minHeight: 48,
              '&.Mui-selected': { color: SAGE[700], fontWeight: 600 },
            },
            '& .MuiTabs-indicator': { bgcolor: SAGE[500], height: 2 },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {dirtyTabs[tab.label.toLowerCase()] && (
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: WARM.accent }} />
                  )}
                  {tab.count !== null
                    ? `${tab.label} (${tab.count})`
                    : tab.label}
                </Box>
              }
            />
          ))}
        </Tabs>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          <PersonalInfoTab
            personal={profile.personal}
            onUpdate={updatePersonalSection}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <SummariesTab
            summaries={profile.summaries}
            onUpdate={updateSummariesSection}
            onDirtyChange={(isDirty) => handleTabDirtyChange('summaries', isDirty)}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          <ExperienceTab
            experience={profile.experience}
            onUpdate={updateExperienceSection}
            onDirtyChange={(isDirty) => handleTabDirtyChange('experience', isDirty)}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          <HighlightsTab
            highlights={profile.highlights}
            onUpdate={updateHighlightsSection}
            onDirtyChange={(isDirty) => handleTabDirtyChange('highlights', isDirty)}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          <EducationTab
            education={profile.education}
            onUpdate={updateEducationSection}
            onDirtyChange={(isDirty) => handleTabDirtyChange('education', isDirty)}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          <SkillsTab
            skills={profile.skills}
            onUpdate={updateSkillsSection}
            onDirtyChange={(isDirty) => handleTabDirtyChange('skills', isDirty)}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={6}>
          <ProjectsTab
            projects={profile.projects}
            onUpdate={updateProjectsSection}
            onDirtyChange={(isDirty) => handleTabDirtyChange('projects', isDirty)}
          />
        </TabPanel>
      </Card>

      {/* Archive & Reset Confirmation Dialog */}
      <Dialog
        open={archiveDialogOpen}
        onClose={() => setArchiveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          🗄️ Archive & Reset
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to archive everything?
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" gutterBottom>
              This will:
            </Typography>
            <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
              <li>Move all your data and documents to a timestamped archive folder</li>
              <li>Reset your profile to a fresh, empty state</li>
              <li>Preserve all archived data (nothing is permanently deleted)</li>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setArchiveDialogOpen(false)}
            disabled={archiving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleArchiveAndReset}
            color="error"
            variant="contained"
            disabled={archiving}
          >
            {archiving ? 'Archiving...' : 'Yes, Archive Everything'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Resume Import Progress Backdrop */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }}
        open={resumeImporting}
      >
        <CircularProgress color="inherit" size={60} />
        {resumeImportStage && (
          <Typography variant="h6" sx={{ mt: 2 }}>
            {resumeImportStage}
          </Typography>
        )}
      </Backdrop>
    </Box>
  );
};

export default ProfileManager;
